/* eslint-disable react-refresh/only-export-components */
import type {
  SailPlotExtensionComponent,
  SailPlotExtensionContext,
} from "@heiselanalytics/sailplot";
import type { LegalOperator } from "../config/legal";
import { PageActions } from "./PageActions";

interface RefundPolicyPageContext {
  operator: LegalOperator;
}

const PADDLE_BUYER_SUPPORT_URL = "https://paddle.net/";
const PADDLE_BUYER_TERMS_URL = "https://www.paddle.com/legal/buyer-terms";
const PADDLE_REFUND_POLICY_URL = "https://www.paddle.com/legal/refund-policy";

const copy = {
  en: {
    eyebrow: "Legal",
    title: "Cancellation, withdrawal, and refund policy",
    intro:
      "This policy distinguishes cancellation of a subscription, the statutory right of withdrawal, voluntary refunds, and remedies for defects. Mandatory rights always take precedence.",
    sections: [
      {
        id: "merchant",
        title: "Paddle as merchant of record",
        paragraphs: [
          "Paid SailPlot purchases are processed by Paddle as merchant of record and authorised reseller. Paddle administers payment, invoices, taxes, subscriptions, chargebacks, and payment refunds. Heisel Analytics supplies the SailPlot branding, implementation, hosting, and operation.",
          "Paddle's buyer terms and refund policy apply to the transaction. They supplement this information but cannot limit mandatory rights. Sensitive payment credentials are entered directly in Paddle Checkout and are not received by SailPlot.",
        ],
      },
      {
        id: "cancellation",
        title: "Cancelling a subscription",
        paragraphs: [
          "Cancellation prevents a future renewal. It is not the same as withdrawing from a recently concluded contract and does not automatically reverse a payment already due. The Paddle customer portal shows the subscription, next billing date, invoices, and available cancellation controls. Paddle normally confirms the effective end date and keeps access available until the end of the paid period.",
          "Mandatory rights to ordinary or extraordinary termination and mandatory requirements for an online cancellation process remain unaffected. A technical portal route does not restrict a legally permitted notice sent by another valid method.",
        ],
      },
      {
        id: "withdrawal",
        title: "Withdrawal instructions for consumers",
        paragraphs: [
          "If you are a consumer and a statutory right of withdrawal applies, you may withdraw from the distance contract within fourteen days without giving a reason. The period generally begins when the relevant contract is concluded. Country-specific mandatory rules may provide a different or additional right.",
          "To exercise the right, send an unambiguous statement before the deadline to Paddle as the merchant shown in your transaction documents, using Paddle Buyer Support or the contact details in Paddle's buyer terms. You may additionally notify Heisel Analytics at the contact shown below so that product access and implementation can be coordinated. You may use the model form below, but it is not mandatory.",
        ],
      },
      {
        id: "effects",
        title: "Effects of a valid withdrawal",
        paragraphs: [
          "If a valid withdrawal applies, payments subject to withdrawal are repaid in accordance with mandatory law, normally using the original payment method and through Paddle's merchant-of-record process. Payment providers or banks may require additional processing time.",
          "A proportionate amount for services already supplied before withdrawal is payable only where the statutory requirements are met, including a legally effective express request to begin performance before the withdrawal period expires. A right of withdrawal for a service does not expire merely because the application was first opened. For services, early expiry generally requires complete performance plus the consumer's prior express consent and acknowledgement where the law requires them.",
          "If the required request, consent, acknowledgement, or confirmation was not validly obtained, neither this policy nor ordinary use of SailPlot replaces it. Any consequence must be assessed according to the declarations actually recorded during checkout and applicable law.",
        ],
      },
      {
        id: "model",
        title: "Model withdrawal form",
        paragraphs: [
          "To Paddle and Heisel Analytics: I hereby withdraw from the contract concluded by me for the SailPlot branding and hosting package. Ordered on: [date]. Paddle transaction or order reference: [reference]. Consumer's name: [name]. Consumer's address: [address]. Date: [date]. Signature only if submitted on paper.",
          "Send the declaration to Paddle using the contact stated in the transaction documents or at paddle.net. A copy may be sent to Heisel Analytics using the email address below. Keep Paddle's or Heisel Analytics' electronic confirmation as evidence.",
        ],
      },
      {
        id: "refunds",
        title: "Voluntary refunds",
        paragraphs: [
          "Where no statutory repayment or refund right applies, there is no general promise of a voluntary refund for a change of mind, non-use, an ordinary cancellation, or a short and insignificant interruption. Paddle may nevertheless review a request under its current refund policy. Submitting a request does not guarantee a full or partial refund.",
          "This limitation does not affect statutory rights of withdrawal, repair, re-performance, price reduction, termination, damages, or repayment, nor any mandatory right granted by Paddle's role as merchant of record.",
        ],
      },
      {
        id: "defects",
        title: "Non-delivery, defects, and incorrect charges",
        paragraphs: [
          "If a configured instance is not delivered, materially differs from the agreed configuration, or has a persistent technical defect, contact Heisel Analytics with enough detail to investigate. Heisel Analytics may first attempt correction or re-performance within a reasonable period. Mandatory remedies remain available if correction fails, is refused, or is unreasonable.",
          "An incorrect or duplicate charge, unrecognised payment, refund, chargeback, or subscription status should also be reported to Paddle with the transaction reference. A delayed or duplicate webhook alone does not determine the customer's legal payment status.",
        ],
      },
      {
        id: "effect",
        title: "Effect on the customized instance",
        paragraphs: [
          "When a subscription validly ends, the paid branding, hosting, customer address, and customized instance may be deactivated at the confirmed end of service. Following a valid withdrawal, refund, reversal, or chargeback, access may be deactivated once the transaction and applicable rights have been verified. Records that must be retained for legal, accounting, fraud-prevention, or defence purposes may remain subject to the Privacy Policy.",
        ],
      },
    ],
    paddleTerms: "Paddle buyer terms",
    paddlePolicy: "Paddle refund policy",
    paddleSupport: "Open Paddle Buyer Support",
    manageSubscription: "Manage subscription & invoices",
    contact: "Contact for the SailPlot service",
    updated: "Last updated: 14 August 2026",
  },
  de: {
    eyebrow: "Rechtliches",
    title: "Kündigung, Widerruf und Rückerstattung",
    intro:
      "Diese Richtlinie unterscheidet zwischen der Kündigung eines Abonnements, dem gesetzlichen Widerrufsrecht, freiwilligen Rückerstattungen und Rechten bei Mängeln. Zwingende Rechte haben stets Vorrang.",
    sections: [
      {
        id: "merchant",
        title: "Paddle als Merchant of Record",
        paragraphs: [
          "Kostenpflichtige SailPlot-Käufe werden durch Paddle als Merchant of Record und autorisierten Wiederverkäufer abgewickelt. Paddle verwaltet Zahlung, Rechnungen, Steuern, Abonnements, Chargebacks und Zahlungsrückerstattungen. Heisel Analytics erbringt SailPlot-Branding, Umsetzung, Hosting und Betrieb.",
          "Für die Transaktion gelten die Paddle-Käuferbedingungen und die Paddle-Rückerstattungsrichtlinie. Sie ergänzen diese Informationen, können zwingende Rechte aber nicht beschränken. Sensible Zahlungsdaten werden direkt im Paddle Checkout eingegeben und nicht von SailPlot empfangen.",
        ],
      },
      {
        id: "cancellation",
        title: "Kündigung eines Abonnements",
        paragraphs: [
          "Eine Kündigung verhindert eine künftige Verlängerung. Sie ist nicht dasselbe wie der Widerruf eines kürzlich geschlossenen Vertrags und macht eine bereits fällige Zahlung nicht automatisch rückgängig. Im Paddle-Kundenportal werden Abonnement, nächster Abrechnungstermin, Rechnungen und verfügbare Kündigungsfunktionen angezeigt. Paddle bestätigt normalerweise den Beendigungszeitpunkt; der Zugriff bleibt bis zum Ende des bezahlten Zeitraums bestehen.",
          "Zwingende Rechte zur ordentlichen oder außerordentlichen Kündigung und zwingende Anforderungen an einen Online-Kündigungsprozess bleiben unberührt. Ein technischer Portalweg schließt eine gesetzlich zulässige Erklärung auf anderem wirksamen Weg nicht aus.",
        ],
      },
      {
        id: "withdrawal",
        title: "Widerrufsbelehrung für Verbraucher",
        paragraphs: [
          "Wenn du Verbraucher bist und ein gesetzliches Widerrufsrecht besteht, kannst du den im Fernabsatz geschlossenen Vertrag binnen vierzehn Tagen ohne Angabe von Gründen widerrufen. Die Frist beginnt grundsätzlich mit Abschluss des betreffenden Vertrags. Zwingendes Recht deines Landes kann ein abweichendes oder zusätzliches Recht vorsehen.",
          "Zur Ausübung des Widerrufsrechts musst du vor Ablauf der Frist eine eindeutige Erklärung an Paddle als den in deinen Transaktionsunterlagen genannten Händler senden. Nutze hierfür den Paddle-Käufersupport oder die Kontaktangaben in den Paddle-Käuferbedingungen. Du kannst zusätzlich Heisel Analytics über den unten genannten Kontakt informieren, damit Produktzugriff und Umsetzung koordiniert werden. Das nachfolgende Musterformular kann, muss aber nicht verwendet werden.",
        ],
      },
      {
        id: "effects",
        title: "Folgen eines wirksamen Widerrufs",
        paragraphs: [
          "Bei einem wirksamen Widerruf werden widerrufsbetroffene Zahlungen nach zwingendem Recht und normalerweise über das ursprüngliche Zahlungsmittel im Merchant-of-Record-Verfahren von Paddle zurückgezahlt. Zahlungsdienstleister oder Banken können zusätzliche Bearbeitungszeit benötigen.",
          "Ein anteiliger Betrag für bereits vor dem Widerruf erbrachte Dienstleistungen ist nur geschuldet, wenn die gesetzlichen Voraussetzungen erfüllt sind. Dazu gehört insbesondere ein rechtlich wirksames ausdrückliches Verlangen, vor Ende der Widerrufsfrist mit der Leistung zu beginnen. Das Widerrufsrecht bei einer Dienstleistung erlischt nicht allein mit dem ersten Öffnen der Anwendung. Ein vorzeitiges Erlöschen setzt grundsätzlich vollständige Leistung und, soweit gesetzlich erforderlich, vorherige ausdrückliche Zustimmung und Kenntnisnahme des Verbrauchers voraus.",
          "Wurden das erforderliche Verlangen, die Zustimmung, Kenntnisnahme oder Bestätigung nicht wirksam eingeholt, werden sie weder durch diese Richtlinie noch durch die normale Nutzung von SailPlot ersetzt. Die Folgen richten sich nach den tatsächlich im Bestellprozess dokumentierten Erklärungen und dem anwendbaren Recht.",
        ],
      },
      {
        id: "model",
        title: "Muster-Widerrufsformular",
        paragraphs: [
          "An Paddle und Heisel Analytics: Hiermit widerrufe ich den von mir abgeschlossenen Vertrag über das SailPlot-Branding- und Hosting-Paket. Bestellt am: [Datum]. Paddle-Transaktions- oder Bestellreferenz: [Referenz]. Name des Verbrauchers: [Name]. Anschrift des Verbrauchers: [Anschrift]. Datum: [Datum]. Unterschrift nur bei Mitteilung auf Papier.",
          "Sende die Erklärung an Paddle über den in den Transaktionsunterlagen genannten Kontakt oder paddle.net. Eine Kopie kann über die unten genannte E-Mail-Adresse an Heisel Analytics gesendet werden. Bewahre die elektronische Bestätigung von Paddle oder Heisel Analytics als Nachweis auf.",
        ],
      },
      {
        id: "refunds",
        title: "Freiwillige Rückerstattungen",
        paragraphs: [
          "Soweit kein gesetzlicher Rückzahlungs- oder Erstattungsanspruch besteht, wird keine allgemeine freiwillige Rückerstattung bei Umentscheiden, Nichtnutzung, ordentlicher Kündigung oder einer kurzen und unerheblichen Störung zugesagt. Paddle kann eine Anfrage dennoch nach seiner jeweils aktuellen Rückerstattungsrichtlinie prüfen. Die Einreichung garantiert keine vollständige oder teilweise Rückerstattung.",
          "Diese Begrenzung berührt weder gesetzliche Widerrufs-, Nacherfüllungs-, Minderungs-, Kündigungs-, Schadensersatz- oder Rückzahlungsrechte noch zwingende Rechte aus Paddles Rolle als Merchant of Record.",
        ],
      },
      {
        id: "defects",
        title: "Nichtbereitstellung, Mängel und falsche Belastungen",
        paragraphs: [
          "Wird eine konfigurierte Instanz nicht bereitgestellt, weicht sie wesentlich von der vereinbarten Konfiguration ab oder hat sie einen dauerhaften technischen Mangel, kontaktiere Heisel Analytics mit ausreichenden Angaben zur Prüfung. Heisel Analytics kann zunächst innerhalb angemessener Frist eine Korrektur oder erneute Leistung versuchen. Zwingende Rechte bleiben bestehen, wenn die Abhilfe fehlschlägt, verweigert wird oder unzumutbar ist.",
          "Eine falsche oder doppelte Belastung, nicht erkannte Zahlung, Rückerstattung, ein Chargeback oder unzutreffender Abonnementstatus sollte zusätzlich mit der Transaktionsreferenz an Paddle gemeldet werden. Ein verzögerter oder doppelter Webhook bestimmt für sich genommen nicht den rechtlichen Zahlungsstatus.",
        ],
      },
      {
        id: "effect",
        title: "Folgen für die personalisierte Instanz",
        paragraphs: [
          "Endet ein Abonnement wirksam, können das bezahlte Branding, Hosting, die Kundenadresse und die personalisierte Instanz zum bestätigten Leistungsende deaktiviert werden. Nach wirksamem Widerruf, Rückerstattung, Rückbelastung oder Chargeback kann der Zugriff deaktiviert werden, sobald Transaktion und anwendbare Rechte geprüft sind. Unterlagen, die aus rechtlichen, buchhalterischen, betrugspräventiven oder Verteidigungsgründen aufbewahrt werden müssen, können nach Maßgabe der Datenschutzerklärung fortbestehen.",
        ],
      },
    ],
    paddleTerms: "Paddle-Käuferbedingungen",
    paddlePolicy: "Paddle-Rückerstattungsrichtlinie",
    paddleSupport: "Paddle-Käufersupport öffnen",
    manageSubscription: "Abonnement & Rechnungen verwalten",
    contact: "Kontakt zur SailPlot-Leistung",
    updated: "Stand: 14. August 2026",
  },
} as const;

function RefundPolicyPage({
  language,
  navigate,
  operator,
}: SailPlotExtensionContext & RefundPolicyPageContext) {
  const text = copy[language];

  return (
    <article className="platform-page platform-reference-page platform-legal-page">
      <header>
        <span className="platform-page-eyebrow">{text.eyebrow}</span>
        <h1>{text.title}</h1>
        <p>{text.intro}</p>
      </header>

      <div className="platform-legal-sections">
        {text.sections.map((section) => (
          <section key={section.id}>
            <h2>{section.title}</h2>
            {section.paragraphs.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}

            {section.id === "merchant" && (
              <p className="platform-legal-external-links">
                <a
                  href={PADDLE_BUYER_TERMS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {text.paddleTerms} ↗
                </a>
                <a
                  href={PADDLE_REFUND_POLICY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {text.paddlePolicy} ↗
                </a>
              </p>
            )}
            {section.id === "cancellation" && (
              <button
                type="button"
                className="platform-legal-link-button"
                onClick={() => navigate("/manage-subscription")}
              >
                {text.manageSubscription}
              </button>
            )}
            {section.id === "withdrawal" && (
              <p>
                <a
                  href={PADDLE_BUYER_SUPPORT_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {text.paddleSupport} ↗
                </a>
              </p>
            )}
          </section>
        ))}

        <section>
          <h2>{text.contact}</h2>
          <p>
            <a href={`mailto:${operator.email}`}>{operator.email}</a>
            {operator.phone && (
              <>
                <br />
                <a href={`tel:${operator.phone.replace(/\s/gu, "")}`}>
                  {operator.phone}
                </a>
              </>
            )}
          </p>
        </section>
      </div>

      <small className="platform-legal-updated">{text.updated}</small>
      <PageActions language={language} navigate={navigate} />
    </article>
  );
}

export const createRefundPolicyPage = (
  legalContext: RefundPolicyPageContext,
): SailPlotExtensionComponent =>
  function TenantRefundPolicyPage(context) {
    return <RefundPolicyPage {...context} {...legalContext} />;
  };
